import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { MapPin, Clock, Car, Phone, ArrowLeftRight } from 'lucide-react';
import { POLLING_INTERVAL_MS, DEFAULT_EXTEND_MINUTES } from '@/constants';

const VisitorPass = () => {
  const { booking_id } = useParams();
  const [pass, setPass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extendMinutes, setExtendMinutes] = useState(DEFAULT_EXTEND_MINUTES);

  const fetchPass = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/visitor/pass/${booking_id}`);
      setPass(res.data);
    } catch (error) {
      toast.error('Failed to load pass');
    } finally {
      setLoading(false);
    }
  }, [booking_id]);

  useEffect(() => {
    fetchPass();
    const interval = setInterval(fetchPass, POLLING_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchPass]);

  const handleExtend = async () => {
    try {
      const res = await axios.post(`${API}/visitor/extend`, {
        booking_id: booking_id,
        additional_minutes: extendMinutes
      });
      toast.success(`Extended by ${extendMinutes} minutes! New exit: ${new Date(res.data.new_estimated_exit).toLocaleTimeString()}`);
      fetchPass();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Extension failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F4F4F5' }}>
        <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>Loading...</p>
      </div>
    );
  }

  if (!pass) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F4F4F5' }}>
        <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#DC2626' }}>Pass not found</p>
      </div>
    );
  }

  const { booking, spot } = pass;

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{
        backgroundColor: '#F4F4F5',
        backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/b70eba2e-88eb-4f5d-9ebc-ce80d62e76ef/images/f79b311aade1fbb5b4cc974422e77eca7438edd41b46e5f351e6d784d292242f.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div
        className="w-full max-w-md p-8 border"
        style={{ backgroundColor: '#FFFFFF', borderColor: '#09090B', borderWidth: '2px' }}
        data-testid="visitor-pass-card"
      >
        {/* Reallocation Banner */}
        {booking.reallocated && (
          <div className="mb-6 p-4" style={{ backgroundColor: '#DBEAFE', border: '2px solid #3B82F6' }} data-testid="reallocation-banner">
            <div className="flex items-center gap-2 mb-2">
              <ArrowLeftRight size={20} style={{ color: '#1E40AF' }} />
              <p className="font-bold text-sm" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#1E40AF' }}>
                Spot Reassigned
              </p>
            </div>
            <p className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#1E3A5F' }}>
              Your spot has been changed to <strong>{spot?.spot_number}</strong>.
              {booking.reallocation_reason && (
                <span className="block mt-1 text-xs" style={{ color: '#3B82F6' }}>
                  Reason: {booking.reallocation_reason}
                </span>
              )}
            </p>
            <p className="text-xs mt-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#1E40AF' }}>
              No additional charges apply.
            </p>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#FBBF24' }}>
            <Car size={40} style={{ color: '#09090B' }} />
          </div>
          <h1
            className="text-2xl sm:text-3xl tracking-tight font-bold mb-2"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}
            data-testid="visitor-pass-title"
          >
            Digital Parking Pass
          </h1>
          <p className="text-xs tracking-[0.2em] uppercase font-bold" style={{ color: '#71717A', fontFamily: 'IBM Plex Sans, sans-serif' }}>
            ParkShare
          </p>
        </div>

        {/* Vehicle Info */}
        <div className="mb-6 p-4 border" style={{ borderColor: '#E4E4E7' }}>
          <p className="text-xs tracking-[0.2em] uppercase font-bold mb-2" style={{ color: '#71717A' }}>Vehicle Number</p>
          <p className="text-3xl font-black" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }} data-testid="vehicle-number">
            {booking.vehicle_number}
          </p>
        </div>

        {/* Parking Spot */}
        <div className="mb-6 p-4" style={{ backgroundColor: booking.reallocated ? '#3B82F6' : '#FBBF24' }}>
          <div className="flex items-center gap-3 mb-2">
            <MapPin size={24} style={{ color: booking.reallocated ? '#FFFFFF' : '#09090B' }} />
            <p className="text-xs tracking-[0.2em] uppercase font-bold" style={{ color: booking.reallocated ? '#FFFFFF' : '#09090B' }}>
              {booking.reallocated ? 'New Parking Spot' : 'Your Parking Spot'}
            </p>
          </div>
          <p className="text-4xl font-black" style={{ fontFamily: 'JetBrains Mono, monospace', color: booking.reallocated ? '#FFFFFF' : '#09090B' }} data-testid="spot-number">
            {spot?.spot_number}
          </p>
        </div>

        {/* Time Info */}
        <div className="space-y-4 mb-6">
          <div className="flex items-center justify-between p-3 border" style={{ borderColor: '#E4E4E7' }}>
            <div className="flex items-center gap-3">
              <Clock size={20} style={{ color: '#71717A' }} />
              <span style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>Check-In</span>
            </div>
            <span style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B', fontWeight: 'bold' }} data-testid="check-in-time">
              {new Date(booking.check_in).toLocaleTimeString()}
            </span>
          </div>

          <div className="flex items-center justify-between p-3 border" style={{ borderColor: '#E4E4E7' }}>
            <div className="flex items-center gap-3">
              <Clock size={20} style={{ color: '#71717A' }} />
              <span style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>Exit By</span>
            </div>
            <span style={{ fontFamily: 'JetBrains Mono, monospace', color: '#DC2626', fontWeight: 'bold' }} data-testid="exit-time">
              {new Date(booking.estimated_exit).toLocaleTimeString()}
            </span>
          </div>

          <div className="flex items-center justify-between p-3 border" style={{ borderColor: '#E4E4E7' }}>
            <div className="flex items-center gap-3">
              <Phone size={20} style={{ color: '#71717A' }} />
              <span style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>Visiting</span>
            </div>
            <span style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B', fontWeight: 'bold' }} data-testid="flat-visiting">
              {booking.flat_visiting}
            </span>
          </div>
        </div>

        {/* Amount */}
        <div className="mb-6 p-4 text-center" style={{ backgroundColor: '#F4F4F5' }}>
          <p className="text-xs tracking-[0.2em] uppercase font-bold mb-1" style={{ color: '#71717A' }}>Parking Fee</p>
          <p className="text-3xl font-black" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }} data-testid="parking-fee">
            {'\u20B9'}{booking.amount}
          </p>
          {booking.reallocated && (
            <p className="text-xs mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#10B981' }}>
              No additional charges for spot change
            </p>
          )}
        </div>

        {/* Extend Button */}
        {booking.status === 'active' && (
          <div>
            <Button
              onClick={handleExtend}
              data-testid="extend-booking-button"
              className="w-full p-4 transition-transform hover:-translate-y-1"
              style={{ backgroundColor: '#09090B', color: '#FFFFFF', fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 'bold' }}
            >
              Extend by {extendMinutes} minutes
            </Button>
            <p className="text-center mt-3 text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
              Subject to owner's return time
            </p>
          </div>
        )}

        {booking.status === 'completed' && (
          <div className="text-center p-4" style={{ backgroundColor: '#10B981' }}>
            <p className="font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#FFFFFF' }}>
              Checked Out
            </p>
          </div>
        )}

        {booking.status === 'cancelled_by_owner' && (
          <div className="p-4" style={{ backgroundColor: '#FEF2F2', border: '2px solid #DC2626' }} data-testid="cancelled-banner">
            <p className="font-bold text-center mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#991B1B' }}>
              Parking Cancelled
            </p>
            <p className="text-sm text-center" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#7F1D1D' }}>
              The owner has reclaimed this spot and no alternative parking is available.
              Please visit the security gate for assistance and refund.
            </p>
            {booking.reallocation_reason && (
              <p className="text-xs text-center mt-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#A1A1AA' }}>
                {booking.reallocation_reason}
              </p>
            )}
          </div>
        )}

        {/* Instructions */}
        <div className="mt-6 p-4 border" style={{ borderColor: '#E4E4E7' }}>
          <p className="text-xs tracking-[0.2em] uppercase font-bold mb-2" style={{ color: '#71717A' }}>Instructions for Visitors</p>
          <ul style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B', fontSize: '14px', listStyle: 'disc', paddingLeft: '20px' }} className="space-y-1.5">
            <li>Obey society specific rules.</li>
            <li>Parking slot has been issued for visiting a flat.</li>
            <li>Do not use parking space for leisure.</li>
            <li style={{ fontWeight: '800', fontSize: '15px' }}>Please scan the QR code at exit gate.</li>
            <li>Overstay charges: {'\u20B9'}2/minute</li>
            {booking.reallocated && <li style={{ color: '#3B82F6' }}>Please park at your new assigned spot.</li>}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default VisitorPass;
